import type { TimeValue } from '@/types';

/**
 * Time-to-angle conversion utilities.
 * All angles are in degrees, measured clockwise from 12 o'clock (0° = up).
 */

/**
 * Calculate hour hand angle.
 * Moves 30° per hour (360° / 12), plus continuous movement based on minutes.
 */
export const getHourAngle = (hours: number, minutes: number): number => {
  return ((hours % 12) + minutes / 60) * 30;
};

/**
 * Calculate minute hand angle.
 * Moves 6° per minute (360° / 60), plus continuous movement based on seconds.
 */
export const getMinuteAngle = (minutes: number, seconds: number): number => {
  return (minutes + seconds / 60) * 6;
};

/**
 * Calculate second hand angle.
 * Moves 6° per second (360° / 60), plus continuous movement based on milliseconds.
 */
export const getSecondAngle = (seconds: number, ms: number): number => {
  return (seconds + ms / 1000) * 6;
};

/**
 * Get all hand angles from a TimeValue.
 */
export const getHandAngles = (
  time: TimeValue
): { hour: number; minute: number; second: number } => ({
  hour: getHourAngle(time.hours, time.minutes),
  minute: getMinuteAngle(time.minutes, time.seconds),
  second: getSecondAngle(time.seconds, time.ms),
});
